import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';

import ConfirmDialog from '../components/admin/ConfirmDialog';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { useLeadStatusOptions, leadStatusLabel, leadStatusColors } from '../hooks/useLeadStatusOptions';
import { useDanismanlarOptions, danismanOptionsList } from '../hooks/useDanismanlarOptions';
import { trNowLocalInput, trNowLocalDateInput } from '../utils/trDateTime';
import {
  Trash2, Upload, Search, ChevronLeft, ChevronRight, X, Download,
  Phone, MessageSquare, Users, Mail, MessageCircle,
  CheckSquare, Square, UserCheck, ClipboardList, ExternalLink, CheckCircle2,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../components/ui/sheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS = 'w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none';
const LBL_CLS = 'text-[10px] font-bold text-[var(--text-muted)] mb-1 block';

const GORUSME_KANAL_OPTIONS = [
  { value: 'telefon',   labelTr: 'Telefon',   icon: Phone },
  { value: 'whatsapp',  labelTr: 'WhatsApp',  icon: MessageSquare },
  { value: 'yuz_yuze',  labelTr: 'Yüz Yüze',  icon: Users },
  { value: 'sms',       labelTr: 'SMS',       icon: Mail },
  { value: 'email',     labelTr: 'E-posta',   icon: Mail },
];

function LeadStatusBadge({ status, options, className }) {
  const label = leadStatusLabel(status, options);
  const { bg, text, border } = leadStatusColors(status);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${bg} ${text} ${border} ${className || ''}`}>
      {status === 'donusturuldu'
        ? <CheckCircle2 size={12} />
        : <span className={`w-1.5 h-1.5 rounded-full ${text.replace('text-', 'bg-')}`} />}
      {label}
    </span>
  );
}

function KanalIcon({ kanal }) {
  const opt = GORUSME_KANAL_OPTIONS.find((o) => o.value === kanal);
  const Icon = opt ? opt.icon : MessageCircle;
  return <Icon size={14} className="inline mr-1 text-[var(--text-muted)]" />;
}

// ─── Import result modal ──────────────────────────────────────────────────────
function ImportResultModal({ result, lang, onClose }) {
  const [showDupDetail, setShowDupDetail] = useState(false);
  const [showErrDetail, setShowErrDetail] = useState(false);
  if (!result) return null;
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const { imported = 0, skipped_duplicates = [], errors = [] } = result;
  const byReason = {};
  skipped_duplicates.forEach((s) => {
    byReason[s.sebep] = (byReason[s.sebep] || 0) + 1;
  });
  const reasonLabel = (r) => ({
    dosya_ici:      tr('Dosya içinde tekrar', 'Duplicate within file'),
    lead_mevcut:    tr('Kayıt zaten mevcut', 'Lead already exists'),
    ogrenci_mevcut: tr('Öğrenci olarak kayıtlı', 'Already a student'),
  }[r] || r);
  const summaryText = tr(
    `${imported} kayıt eklendi, ${skipped_duplicates.length} mükerrer atlandı, ${errors.length} hata.`,
    `${imported} records imported, ${skipped_duplicates.length} duplicates skipped, ${errors.length} errors.`
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-lg mb-3">{tr('İçe Aktarma Sonucu', 'Import Result')}</h3>
        <p className="sr-only" role="status" aria-live="polite">{summaryText}</p>
        <div className="space-y-2 text-sm mb-4">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-500 inline-block" />
            <span className="font-semibold">{tr('Aktarılan:', 'Imported:')} <strong>{imported}</strong></span>
          </div>
          {skipped_duplicates.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-3 h-3 rounded-full bg-amber-400 inline-block" />
                <span className="font-semibold">{tr('Atlanan (mükerrer):', 'Skipped (duplicates):')} <strong>{skipped_duplicates.length}</strong></span>
              </div>
              {Object.entries(byReason).map(([reason, cnt]) => (
                <div key={reason} className="ml-5 text-xs text-[var(--text-muted)]">
                  — {reasonLabel(reason)}: <strong>{cnt}</strong>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setShowDupDetail((v) => !v)}
                className="ml-5 mt-1 text-xs font-bold text-[var(--brand-primary)] hover:underline"
              >
                {showDupDetail ? tr('Detayı gizle', 'Hide details') : tr('Satır satır göster', 'Show row details')}
              </button>
              {showDupDetail && (
                <ul className="ml-5 mt-1 space-y-0.5 text-xs text-[var(--text-muted)] max-h-32 overflow-y-auto">
                  {skipped_duplicates.map((s, i) => (
                    <li key={i}>{tr('Satır', 'Row')} {s.row}: {s.telefon || '—'} — {reasonLabel(s.sebep)}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {errors && errors.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-3 h-3 rounded-full bg-red-500 inline-block" />
                <span className="font-semibold">{tr('Hata:', 'Errors:')} <strong>{errors.length}</strong></span>
              </div>
              <button
                type="button"
                onClick={() => setShowErrDetail((v) => !v)}
                className="ml-5 mt-1 text-xs font-bold text-[var(--brand-primary)] hover:underline"
              >
                {showErrDetail ? tr('Detayı gizle', 'Hide details') : tr('Satır satır göster', 'Show row details')}
              </button>
              {showErrDetail && (
                <ul className="ml-5 mt-1 space-y-0.5 text-xs text-[var(--text-muted)] max-h-32 overflow-y-auto">
                  {errors.map((e, i) => (
                    <li key={i}>{tr('Satır', 'Row')} {e.row}: {e.error}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <button onClick={onClose} className="px-5 py-2 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-2 border-[var(--brand-primary-shadow)] hover:opacity-90">
            {tr('Tamam', 'OK')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Görüşme paneli ───────────────────────────────────────────────────────────
function GorusmePaneli({ lead, lang, statusOptions, authHeaders, onClose, onGorusmeAdded }) {
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const toast = useToast();
  const [gorusmeler, setGorusmeler] = useState([]);
  const [gLoading, setGLoading] = useState(false);
  const [kanal, setKanal] = useState('telefon');
  const [sonuc, setSonuc] = useState(statusOptions && statusOptions[0] ? statusOptions[0].value : 'yeni');
  const [not, setNot] = useState('');
  const [tarih, setTarih] = useState(() => trNowLocalInput());
  const [saving, setSaving] = useState(false);

  const fetchGorusmeler = useCallback(async () => {
    if (!lead) return;
    setGLoading(true);
    try {
      const r = await axios.get(`${API}/admin/leads/${lead.lead_id}/gorusmeler?page=1&page_size=20`, { headers: authHeaders() });
      setGorusmeler(r.data.gorusmeler || []);
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setGLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead, lang]);

  useEffect(() => { fetchGorusmeler(); }, [fetchGorusmeler]);

  const addGorusme = async () => {
    if (!lead) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/leads/${lead.lead_id}/gorusmeler`, { kanal, sonuc, not, tarih }, { headers: authHeaders() });
      toast.success(tr('Görüşme eklendi', 'Note added'));
      setNot('');
      setTarih(trNowLocalInput());
      fetchGorusmeler();
      if (onGorusmeAdded) onGorusmeAdded();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setSaving(false); }
  };

  const fmtDate = (d) => {
    if (!d) return '-';
    try { return new Date(d).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return d; }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <SheetTitle className="text-base font-bold">{tr('Görüşme Notları', 'Call Notes')}</SheetTitle>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">{lead?.ad_soyad}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={18} /></button>
      </div>

      {/* Yeni görüşme formu */}
      <div className="bg-gray-50 rounded-xl p-3 border-2 border-[var(--border-light)] mb-4">
        <p className="text-[10px] font-bold text-[var(--text-muted)] mb-2">{tr('YENİ GÖRÜŞME', 'NEW NOTE')}</p>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <label className={LBL_CLS}>{tr('Kanal', 'Channel')}</label>
            <select value={kanal} onChange={(e) => setKanal(e.target.value)} className={INPUT_CLS}>
              {GORUSME_KANAL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.labelTr}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={LBL_CLS}>{tr('Sonuç', 'Outcome')}</label>
            <select value={sonuc} onChange={(e) => setSonuc(e.target.value)} className={INPUT_CLS}>
              {statusOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mb-2">
          <label className={LBL_CLS}>{tr('Not', 'Note')}</label>
          <textarea value={not} onChange={(e) => setNot(e.target.value)} rows={2}
            className={`${INPUT_CLS} resize-none`} placeholder={tr('Notunuzu girin...', 'Enter note...')} />
        </div>
        <div className="mb-2">
          <label className={LBL_CLS}>{tr('Tarih / Saat', 'Date / Time')}</label>
          <input type="datetime-local" value={tarih} onChange={(e) => setTarih(e.target.value)} className={INPUT_CLS} />
        </div>
        <button onClick={addGorusme} disabled={saving} className="w-full py-2 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-2 border-[var(--brand-primary-shadow)] hover:opacity-90 disabled:opacity-50">
          {saving ? '...' : tr('Görüşme Ekle', 'Add Note')}
        </button>
      </div>

      {/* Görüşme geçmişi */}
      <div className="flex-1 overflow-y-auto space-y-2">
        {gLoading && <div className="text-center text-sm text-[var(--text-muted)] py-4">{tr('Yükleniyor...', 'Loading...')}</div>}
        {!gLoading && gorusmeler.length === 0 && (
          <div className="text-center text-sm text-[var(--text-muted)] py-4">{tr('Henüz görüşme yok', 'No notes yet')}</div>
        )}
        {gorusmeler.map((g, i) => (
          <div key={g.gorusme_id || i} className="bg-white rounded-xl border-2 border-[var(--border-light)] p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1">
                <KanalIcon kanal={g.kanal} />
                <span className="text-xs font-bold">{g.personel_adi || '-'}</span>
              </div>
              <span className="text-[10px] text-[var(--text-muted)]">{fmtDate(g.tarih || g.created_at)}</span>
            </div>
            <div className="flex items-center gap-2 mb-1">
              <LeadStatusBadge status={g.sonuc} options={statusOptions} />
            </div>
            {g.not && <p className="text-xs text-[var(--text-muted)] mt-1">{g.not}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Ana sayfa ────────────────────────────────────────────────────────────────
export default function AdminLeads() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const { options: statusOptions } = useLeadStatusOptions();

  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [fReklam, setFReklam] = useState('');
  const [fArsiv, setFArsiv] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [loading, setLoading] = useState(false);
  // reklam_turu tanımı filtre barındaki "Tüm kaynaklar" select'i için, ayrıca
  // reklam_firmasi tanımı Excel yükleme bloğundaki opsiyonel "Reklam Firması"
  // select'i için tutulur (bkz. Excel Yükleme bloğu, aşağıda).
  const [defs, setDefs] = useState({ reklam_turu: [], reklam_firmasi: [] });
  // Excel yükleme bloğu — Eğitim seçimi için ders/kurs listesi. Eskiden
  // kaldırılan "Yeni Data" formunun beslediği aynı public endpoint (bkz.
  // `git show 53407e6^:frontend/src/pages/AdminLeads.jsx`), yeni bir endpoint
  // uydurulmadı.
  const [courses, setCourses] = useState([]);
  const uploadFileRef = useRef(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDersIlgi, setUploadDersIlgi] = useState('');
  const [uploadDataTarihi, setUploadDataTarihi] = useState(() => trNowLocalDateInput());
  const [uploadReklamFirmasi, setUploadReklamFirmasi] = useState('');
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [templateDownloading, setTemplateDownloading] = useState(false);
  // Atama amaçlı danışman seçimi — HER ZAMAN aktif=tumu (bkz. useDanismanlarOptions
  // dosya başı yorumu). Pasif danışmanlar listeden ÇIKARILMAZ, seçilemez YAPILIR
  // (danismanOptionsList disablePasif:true) — mevcut atamalar görünür kalır.
  const { danismanlar } = useDanismanlarOptions(authHeaders, { includeAdmins: true });

  // Silme confirm
  const [delLead, setDelLead] = useState(null);

  // Toplu atama
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDanId, setBulkDanId] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);

  // Toplu DAGITIM (coklu danisman) — 'tek' | 'dagit'
  const [bulkMode, setBulkMode] = useState('tek');
  const [bulkDanIds, setBulkDanIds] = useState([]);
  // Bos string = sinir yok (hepsi dagitilir). Sayi = kisi basina en fazla N.
  const [perPersonel, setPerPersonel] = useState('');

  // Import sonuç modal
  const [importResult, setImportResult] = useState(null);

  // Görüşme paneli
  const [gorusmeLead, setGorusmeLead] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (search) p.set('search', search);
      if (fReklam) p.set('reklam_turu', fReklam);
      if (fArsiv) p.set('arsiv', fArsiv);
      if (fStatus) p.set('status', fStatus);
      p.set('atanmamis', 'true');
      p.set('page', String(page + 1));
      const r = await axios.get(`${API}/admin/leads?${p.toString()}`, { headers: authHeaders() });
      setLeads(r.data.leads || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setSelectedIds([]);
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, fReklam, fArsiv, fStatus, page, lang]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    axios.get(`${API}/admin/definitions`, { headers: authHeaders() }).then((r) => {
      const all = r.data.definitions || [];
      setDefs({
        reklam_turu: all.filter((d) => d.type === 'reklam_turu'),
        reklam_firmasi: all.filter((d) => d.type === 'reklam_firmasi'),
      });
    }).catch(() => {});
    axios.get(`${API}/courses?limit=100`).then((r) => setCourses(r.data.courses || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doRemove = async () => {
    const id = delLead && delLead.lead_id;
    if (!id) return;
    try {
      await axios.delete(`${API}/admin/leads/${id}`, { headers: authHeaders() });
      setDelLead(null);
      fetchLeads();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  };

  const assign = async (lead, danId) => {
    const d = danismanlar.find((x) => x.user_id === danId);
    try {
      await axios.put(`${API}/admin/leads/${lead.lead_id}`, { atanan_personel_id: danId, atanan_personel_adi: d ? d.name : '' }, { headers: authHeaders() });
      toast.success(tr('Danışmana atandı, Danışman Paneli > Datalar\'da görüntülenebilir', 'Assigned to advisor, now visible under Advisor Panel > Data'));
      fetchLeads();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  };

  // Excel ile toplu data yükleme — dosya seçimi değişince state'e alınır,
  // "Yükle" butonu ayrı bir aksiyon (bkz. onUploadFile). Eğitim (ders_ilgi)
  // UI'da ZORUNLU (backend'de opsiyonel — bkz. routes/leads.py::import_leads
  // docstring'i), Data Tarihi bugün varsayılan gelir, Reklam Firması opsiyonel.
  const onUploadFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    setUploadFile(f || null);
  };

  const onUploadFile = async () => {
    if (!uploadFile || !uploadDersIlgi || uploadingBulk) return;
    setUploadingBulk(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('ders_ilgi', uploadDersIlgi);
      if (uploadDataTarihi) fd.append('data_tarihi', uploadDataTarihi);
      if (uploadReklamFirmasi) fd.append('reklam_firmasi', uploadReklamFirmasi);
      const r = await axios.post(`${API}/admin/leads/import`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } });
      setImportResult(r.data);
      setPage(0);
      fetchLeads();
      setUploadFile(null);
      if (uploadFileRef.current) uploadFileRef.current.value = '';
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setUploadingBulk(false); }
  };

  const onDownloadTemplate = async () => {
    if (templateDownloading) return;
    setTemplateDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/leads/import-template`, { headers: authHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'lead-import-sablonu.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setTemplateDownloading(false); }
  };

  // ── Toplu seçim ──────────────────────────────────────────────────────────────
  const allIds = leads.map((l) => l.lead_id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));

  const toggleAll = () => {
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(allIds);
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const doBulkAssign = async () => {
    if (!bulkDanId || selectedIds.length === 0) {
      toast.error(tr('Danışman ve en az 1 lead seçin', 'Select advisor and at least 1 lead'));
      return;
    }
    setBulkAssigning(true);
    try {
      const r = await axios.post(`${API}/admin/leads/bulk-assign`, { lead_ids: selectedIds, personel_id: bulkDanId }, { headers: authHeaders() });
      toast.success(tr(`${r.data.updated || selectedIds.length} lead danışmana atandı, Danışman Paneli > Datalar'da görüntülenebilir`, `${r.data.updated || selectedIds.length} leads assigned to advisor, now visible under Advisor Panel > Data`));
      setBulkDanId('');
      setSelectedIds([]);
      fetchLeads();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setBulkAssigning(false); }
  };

  const toggleBulkDan = (id) => {
    setBulkDanIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const doBulkDistribute = async () => {
    if (bulkDanIds.length === 0 || selectedIds.length === 0) {
      toast.error(tr('En az 1 danışman ve 1 lead seçin', 'Select at least 1 advisor and 1 lead'));
      return;
    }
    // Bos birakilirsa sinir yok; girilmisse pozitif tam sayi olmali.
    const per = perPersonel.trim() === '' ? null : Number(perPersonel);
    if (per !== null && (!Number.isInteger(per) || per < 1)) {
      toast.error(tr('Kişi başına adet 1 veya daha büyük tam sayı olmalı', 'Per-advisor count must be an integer of 1 or more'));
      return;
    }
    setBulkAssigning(true);
    try {
      const payload = { lead_ids: selectedIds, personel_ids: bulkDanIds };
      if (per !== null) payload.per_personel = per;
      const r = await axios.post(`${API}/admin/leads/bulk-assign-distribute`, payload, { headers: authHeaders() });
      const ozet = (r.data.dagilim || []).map((d) => `${d.personel_adi}: ${d.adet}`).join(' · ');
      toast.success(
        tr(`${r.data.updated} lead dağıtıldı — ${ozet}`, `${r.data.updated} leads distributed — ${ozet}`)
      );
      if (r.data.atanmayan > 0) {
        // DIKKAT: buradaki `toast` ToastContext'ten gelen bir NESNE
        // ({success, error, warning, info}) — cagrilabilir fonksiyon DEGIL.
        // Ciplak `toast(...)` "a is not a function" ile patlar (24 Agustos 2026).
        toast.warning(
          tr(`${r.data.atanmayan} lead kişi başına sınır nedeniyle atanmadı`, `${r.data.atanmayan} leads left unassigned due to the per-advisor cap`)
        );
      }
      setBulkDanIds([]);
      setPerPersonel('');
      setSelectedIds([]);
      fetchLeads();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setBulkAssigning(false); }
  };

  const fmtDate = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-GB'); }
    catch { return d; }
  };

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Data' }]}
        title={tr('Data / Potansiyel Müşteri', 'Data / Leads')}
        subtitle={tr('Excel ile toplu giriş ve danışmana atama', 'Bulk Excel import and advisor assignment')}
      />
      <p className="text-xs text-[var(--text-muted)] font-semibold -mt-3 mb-4">
        {tr('Bu liste yalnızca henüz danışmana atanmamış data\'ları gösterir; atanmış data\'lar Danışman Paneli > Datalar bölümünde görüntülenir.', 'This list only shows leads not yet assigned to an advisor; assigned leads appear under Advisor Panel > Data.')}
      </p>

      {/* Excel ile Data Yükleme */}
      <div className="card-clay p-4 mb-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-bold text-base">{tr('Excel ile Data Yükleme', 'Bulk Data Upload')}</h3>
          <button
            type="button"
            onClick={onDownloadTemplate}
            disabled={templateDownloading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-bold border-2 border-green-600 hover:bg-green-600 disabled:opacity-50"
          >
            <Download size={14} /> {templateDownloading ? '...' : tr('Örnek İndir', 'Download Template')}
          </button>
        </div>

        <p className="text-xs text-[var(--text-muted)] font-semibold mb-3">
          {tr(
            'Beklenen kolonlar: Ad Soyad, Telefon, Email, Şehir, Meslek, Reklam Türü, Reklam Firması, Eğitim Durumu, Cinsiyet, Açıklama.',
            'Expected columns: Full Name, Phone, Email, City, Profession, Ad Source, Ad Company, Education, Gender, Note.'
          )}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div className="lg:col-span-1">
            <label htmlFor="lead-upload-file" className={LBL_CLS}>{tr('Excel Dosyası', 'Excel File')} <span className="text-red-500">*</span></label>
            <input
              id="lead-upload-file"
              ref={uploadFileRef}
              type="file"
              accept=".xlsx"
              onChange={onUploadFileChange}
              className="w-full text-xs font-semibold file:mr-2 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-[var(--brand-primary)] file:text-white file:text-xs file:font-bold"
            />
          </div>
          <div>
            <label htmlFor="lead-upload-ders" className={LBL_CLS}>{tr('Eğitim', 'Course')} <span className="text-red-500">*</span></label>
            <select
              id="lead-upload-ders"
              value={uploadDersIlgi}
              onChange={(e) => setUploadDersIlgi(e.target.value)}
              className={INPUT_CLS}
            >
              <option value="">{tr('Seçiniz', 'Select')}</option>
              {courses.map((c) => (
                <option key={c.course_id} value={c.title}>{c.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="lead-upload-tarih" className={LBL_CLS}>{tr('Data Tarihi', 'Data Date')}</label>
            <input
              id="lead-upload-tarih"
              type="date"
              value={uploadDataTarihi}
              onChange={(e) => setUploadDataTarihi(e.target.value)}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <label htmlFor="lead-upload-reklam-firmasi" className={LBL_CLS}>{tr('Reklam Firması', 'Ad Company')}</label>
            <select
              id="lead-upload-reklam-firmasi"
              value={uploadReklamFirmasi}
              onChange={(e) => setUploadReklamFirmasi(e.target.value)}
              className={INPUT_CLS}
            >
              <option value="">{tr('Seçiniz', 'Select')}</option>
              {defs.reklam_firmasi.map((o) => <option key={o.def_id} value={o.label_tr}>{o.label_tr}</option>)}
            </select>
          </div>
          <div>
            <button
              type="button"
              onClick={onUploadFile}
              disabled={!uploadFile || !uploadDersIlgi || uploadingBulk}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-blue-500 text-white text-sm font-bold border-2 border-blue-600 hover:bg-blue-600 disabled:opacity-50"
            >
              <Upload size={16} /> {uploadingBulk ? tr('Yükleniyor...', 'Uploading...') : tr('Yükle', 'Upload')}
            </button>
            {!uploadDersIlgi && (
              <p className="text-[10px] text-[var(--text-muted)] font-semibold mt-1">
                {tr('Yüklemek için önce Eğitim seçin.', 'Select a course to enable upload.')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Filtreler */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} placeholder={tr('Ara (ad, telefon, email)...', 'Search...')}
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white font-semibold text-sm focus:border-[var(--brand-primary)] focus:outline-none" />
        </div>
        <select value={fStatus} onChange={(e) => { setFStatus(e.target.value); setPage(0); }} className="px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold">
          <option value="">{tr('Tüm durumlar', 'All statuses')}</option>
          {statusOptions.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select value={fReklam} onChange={(e) => { setFReklam(e.target.value); setPage(0); }} className="px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold">
          <option value="">{tr('Tüm kaynaklar', 'All sources')}</option>
          {defs.reklam_turu.map((o) => <option key={o.def_id} value={o.label_tr}>{o.label_tr}</option>)}
        </select>
        <select value={fArsiv} onChange={(e) => { setFArsiv(e.target.value); setPage(0); }} className="px-3 py-2.5 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold">
          <option value="">{tr('Aktif', 'Active')}</option>
          <option value="true">{tr('Arşivli', 'Archived')}</option>
        </select>
        <span className="text-sm text-[var(--text-muted)] font-semibold">{total} {tr('kayıt', 'leads')}</span>
      </div>

      {/* Toplu atama aksiyonu */}
      {selectedIds.length > 0 && (
        <div className="mb-3 bg-amber-50 border-2 border-amber-200 rounded-xl px-4 py-2.5">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-bold text-amber-700">{selectedIds.length} {tr('seçili', 'selected')}</span>
            {/* Mod anahtari: tek danismana ata / cok danismana dagit */}
            <div className="flex rounded-lg border-2 border-amber-300 overflow-hidden">
              {[['tek', tr('Tek danışman', 'Single advisor')], ['dagit', tr('Dağıt', 'Distribute')]].map(([m, label]) => (
                <button
                  key={m}
                  onClick={() => setBulkMode(m)}
                  data-testid={`bulk-mode-${m}`}
                  className={`px-3 py-1.5 text-xs font-bold ${bulkMode === m ? 'bg-amber-500 text-white' : 'bg-white text-amber-700 hover:bg-amber-100'}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {bulkMode === 'tek' && (
              <>
                <select value={bulkDanId} onChange={(e) => setBulkDanId(e.target.value)} className="px-3 py-1.5 rounded-lg border-2 border-[var(--border-light)] bg-white text-sm font-semibold flex-1 min-w-[160px] max-w-xs">
                  <option value="">{tr('Danışman seçin...', 'Select advisor...')}</option>
                  {danismanOptionsList(danismanlar, tr, { disablePasif: true }).map((o) => (
                    <option key={o.key} value={o.value} disabled={o.disabled}>{o.label}</option>
                  ))}
                </select>
                <button onClick={doBulkAssign} disabled={bulkAssigning || !bulkDanId} className="flex items-center gap-2 px-4 py-1.5 rounded-xl bg-amber-500 text-white text-sm font-bold border-2 border-amber-600 hover:bg-amber-600 disabled:opacity-50">
                  <UserCheck size={16} /> {bulkAssigning ? '...' : tr('Danışmana Ata', 'Assign to Advisor')}
                </button>
              </>
            )}

            <button onClick={() => { setSelectedIds([]); setBulkDanIds([]); }} className="text-xs text-amber-600 font-bold hover:underline ml-auto">{tr('İptal', 'Cancel')}</button>
          </div>

          {bulkMode === 'dagit' && (
            <div className="mt-3 flex gap-4 flex-wrap items-start">
              {/* Coklu danisman secimi */}
              <div className="flex-1 min-w-[220px] max-w-md">
                <div className="text-xs font-bold text-amber-700 mb-1">{tr('Danışmanlar', 'Advisors')}</div>
                <div className="max-h-40 overflow-y-auto rounded-lg border-2 border-[var(--border-light)] bg-white p-2 space-y-1">
                  {danismanOptionsList(danismanlar, tr, { disablePasif: true }).map((o) => (
                    <label key={o.key} className={`flex items-center gap-2 text-sm font-semibold px-1 py-0.5 rounded ${o.disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-amber-50'}`}>
                      <input
                        type="checkbox"
                        disabled={o.disabled}
                        checked={bulkDanIds.includes(o.value)}
                        onChange={() => toggleBulkDan(o.value)}
                        data-testid={`bulk-dan-${o.value}`}
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
                <button
                  onClick={() => setBulkDanIds(danismanOptionsList(danismanlar, tr, { disablePasif: true }).filter((o) => !o.disabled).map((o) => o.value))}
                  className="text-xs text-amber-600 font-bold hover:underline mt-1"
                >
                  {tr('Tümünü seç', 'Select all')}
                </button>
              </div>

              {/* Kisi basina adet + dagit butonu */}
              <div className="min-w-[200px]">
                <div className="text-xs font-bold text-amber-700 mb-1">{tr('Kişi başına adet', 'Per advisor')}</div>
                <input
                  type="number"
                  min="1"
                  value={perPersonel}
                  onChange={(e) => setPerPersonel(e.target.value)}
                  placeholder={tr('boş = hepsini dağıt', 'empty = distribute all')}
                  data-testid="bulk-per-personel"
                  className="w-full px-3 py-1.5 rounded-lg border-2 border-[var(--border-light)] bg-white text-sm font-semibold"
                />
                <div className="text-xs text-amber-700 mt-1.5 font-semibold">
                  {bulkDanIds.length === 0
                    ? tr('Danışman seçilmedi', 'No advisor selected')
                    : (() => {
                        const m = bulkDanIds.length;
                        const per = perPersonel.trim() === '' ? null : Number(perPersonel);
                        const kapasite = per && per > 0 ? m * per : selectedIds.length;
                        const dagitilacak = Math.min(selectedIds.length, kapasite);
                        const artan = selectedIds.length - dagitilacak;
                        return tr(
                          `${m} danışmana ${dagitilacak} lead${artan > 0 ? ` · ${artan} atanmayacak` : ''}`,
                          `${dagitilacak} leads to ${m} advisors${artan > 0 ? ` · ${artan} left out` : ''}`
                        );
                      })()}
                </div>
                <button
                  onClick={doBulkDistribute}
                  disabled={bulkAssigning || bulkDanIds.length === 0}
                  data-testid="bulk-distribute-btn"
                  className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-1.5 rounded-xl bg-amber-500 text-white text-sm font-bold border-2 border-amber-600 hover:bg-amber-600 disabled:opacity-50"
                >
                  <UserCheck size={16} /> {bulkAssigning ? '...' : tr('Danışmanlara Dağıt', 'Distribute to Advisors')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tablo */}
      <div className="card-clay overflow-hidden">
        {loading && <div className="p-3 text-center text-sm text-[var(--text-muted)]">{tr('Yükleniyor...', 'Loading...')}</div>}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-3 w-8">
                  <button onClick={toggleAll} className="text-gray-400 hover:text-gray-600">
                    {allSelected ? <CheckSquare size={16} className="text-[var(--brand-primary)]" /> : <Square size={16} />}
                  </button>
                </th>
                <th className="text-left p-3 font-bold">{tr('Ad Soyad', 'Name')}</th>
                <th className="text-left p-3 font-bold">{tr('İletişim', 'Contact')}</th>
                <th className="text-left p-3 font-bold">{tr('Kaynak', 'Source')}</th>
                <th className="text-left p-3 font-bold">{tr('Şehir', 'City')}</th>
                <th className="text-left p-3 font-bold">{tr('Danışman', 'Advisor')}</th>
                <th className="text-center p-3 font-bold">{tr('Durum', 'Status')}</th>
                <th className="text-center p-3 font-bold">{tr('Görüşme', 'Notes')}</th>
                <th className="text-center p-3 font-bold">{tr('İşlem', 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => {
                const isSel = selectedIds.includes(l.lead_id);
                return (
                  <tr key={l.lead_id} className={`border-t border-[var(--border-light)] hover:bg-gray-50 ${isSel ? 'bg-blue-50' : ''}`}>
                    <td className="p-3">
                      <button onClick={() => toggleOne(l.lead_id)} className="text-gray-400 hover:text-[var(--brand-primary)]">
                        {isSel ? <CheckSquare size={16} className="text-[var(--brand-primary)]" /> : <Square size={16} />}
                      </button>
                    </td>
                    <td className="p-3 font-bold">{l.ad_soyad}</td>
                    <td className="p-3 text-xs"><div>{l.telefon}</div><div className="text-[var(--text-muted)]">{l.email}</div></td>
                    <td className="p-3 text-xs">{l.reklam_turu}{l.reklam_firmasi ? ` / ${l.reklam_firmasi}` : ''}</td>
                    <td className="p-3 text-xs">{l.sehir}</td>
                    <td className="p-3">
                      <select value={l.atanan_personel_id || ''} onChange={(e) => assign(l, e.target.value)} disabled={!!l.donusturulen_user_id}
                        className="text-xs px-2 py-1.5 rounded-lg border-2 border-[var(--border-light)] bg-white font-semibold max-w-[150px]">
                        <option value="">{tr('Atanmadı', 'Unassigned')}</option>
                        {danismanOptionsList(danismanlar, tr, { disablePasif: true }).map((o) => (
                          <option key={o.key} value={o.value} disabled={o.disabled}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3 text-center">
                      {l.donusturulen_user_id
                        ? <LeadStatusBadge status="donusturuldu" options={statusOptions} />
                        : <LeadStatusBadge status={l.status || 'yeni'} options={statusOptions} />}
                    </td>
                    <td className="p-3 text-center">
                      <button onClick={() => setGorusmeLead(l)} className="flex flex-col items-center gap-0.5 mx-auto group">
                        <div className="flex items-center gap-1">
                          <ClipboardList size={15} className="text-[var(--brand-primary)] group-hover:text-[var(--brand-primary-hover)]" />
                          {l.gorusme_sayisi > 0 && (
                            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]">{l.gorusme_sayisi}</span>
                          )}
                        </div>
                        {l.son_gorusme_tarihi && (
                          <span className="text-[9px] text-[var(--text-muted)]">{fmtDate(l.son_gorusme_tarihi)}</span>
                        )}
                      </button>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-1">
                        {l.donusturulen_user_id ? (
                          <a
                            href={`/admin/students/${l.donusturulen_user_id}`}
                            title={tr('Öğrenci Profiline Git', 'Go to Student Profile')}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-bold text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-lg border border-cyan-200"
                          >
                            <ExternalLink size={12} />
                            {tr('Profil', 'Profile')}
                          </a>
                        ) : (
                          <a
                            href={`/admin/students/new-identity?prefill_lead=${l.lead_id}&ad_soyad=${encodeURIComponent(l.ad_soyad || '')}&telefon=${encodeURIComponent(l.telefon || '')}&email=${encodeURIComponent(l.email || '')}`}
                            title={tr('Kimlik Oluştur', 'Create Identity')}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          >
                            <ExternalLink size={15} />
                          </a>
                        )}
                        <button onClick={() => setDelLead(l)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && leads.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-sm text-[var(--text-muted)]">{tr('Atanmamış data yok', 'No unassigned leads')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between p-4 border-t border-[var(--border-light)]">
          <span className="text-sm text-[var(--text-muted)] font-semibold">{total} {tr('kayıt', 'leads')}</span>
          <div className="flex gap-2 items-center">
            <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0 || loading} className="p-2 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40"><ChevronLeft size={16} /></button>
            <span className="px-3 py-2 text-sm font-bold">{page + 1} / {totalPages}</span>
            <button onClick={() => setPage(page + 1)} disabled={page + 1 >= totalPages || loading} className="p-2 rounded-lg border-2 border-[var(--border-light)] disabled:opacity-40"><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      {/* Silme onayı */}
      <ConfirmDialog
        open={!!delLead}
        onOpenChange={(o) => { if (!o) setDelLead(null); }}
        title={tr('Silinsin mi?', 'Delete this lead?')}
        description={delLead ? delLead.ad_soyad : ''}
        variant="danger"
        confirmLabel={tr('Sil', 'Delete')}
        onConfirm={doRemove}
      />

      {/* Import sonucu */}
      {importResult && (
        <ImportResultModal result={importResult} lang={lang} onClose={() => setImportResult(null)} />
      )}

      {/* Görüşme paneli */}
      <Sheet open={!!gorusmeLead} onOpenChange={(o) => { if (!o) { setGorusmeLead(null); fetchLeads(); } }}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="sr-only">
            <SheetTitle>{tr('Görüşme Notları', 'Call Notes')}</SheetTitle>
          </SheetHeader>
          {gorusmeLead && (
            <GorusmePaneli
              lead={gorusmeLead}
              lang={lang}
              statusOptions={statusOptions}
              authHeaders={authHeaders}
              onClose={() => setGorusmeLead(null)}
              onGorusmeAdded={fetchLeads}
            />
          )}
        </SheetContent>
      </Sheet>
    </AdminLayout>
  );
}
